import { useState } from "react";
import useApi from "./useApi";
import { API_ROUTES } from "lib/constants/api-constants";
import { DMS_PATHS } from "lib/constants/dms-path-constants";
import useDocLinks from "./useDocLinks";
import { DMSServices } from "lib/services/document-services";

interface TherapiesState {
  error: string;
  loading: boolean;
  therapies: Therapy[];
  getTherapies: (onSuccess?: (response: Therapy[]) => void) => void;
  getSessionEnabledTherapies: (onSuccess?: (response: Therapy[]) => void) => void;
  getTherapyAnalyticsDetails: (
    therapyId: string,
    onSuccess?: (therapyAnalyticsDetails: TherapyAnalyticsDetails) => void
  ) => void;
  getAllTherapyAnalytics(
    onSuccess?: (therapyAnalytics: TherapyAnalytics[]) => void
  );
  getPractitionerTherapies: (
    practitionerId: string,
    onSuccess?: (response: PractitionerTherapy[]) => void
  ) => void;
  updateTherapy: (
    therapy: Therapy,
    onSuccess?: (response: Therapy) => void
  ) => void;
  deleteTherapy: (
    therapyId: string,
    onSuccess?: () => void
  ) => void;
  addTherapy: (
    therapy: Therapy,
    onSuccess?: (newTherapy: Therapy) => void
  ) => void;
  getTherapyImage: (
    therapyId: string,
    onSuccess?: (url: string) => void
  ) => void;
  resetError: VoidFunction;
  setTherapyImage: (
    therapyId: string,
    therapyImage: File,
    onSuccess?: (url: string) => void
  ) => void;
}

const useTherapies = (): TherapiesState => {
  const {
    createDocLink,
    error: docLinksError,
    isLoading: docLinksLoading,
    getDocLink,
  } = useDocLinks();
  const { getData, loading: getDataLoading } = useApi<Therapy[]>();
  const {
    getData: getTherapyAnalyticsDetailsRequest,
    loading: getTherapyAnalyticsDetailsLoading,
  } = useApi<TherapyAnalyticsDetails>();
  const {
    getData: getPractitionerTherapiesRequest,
    loading: getPractitionerTherapiesLoading,
  } = useApi<PractitionerTherapy[]>();
  const {
    getData: getAllTherapyAnalyticsRequest,
    loading: getAllTherapyAnalyticsRequestLoading,
  } = useApi<TherapyAnalytics[]>();
  const { putData: updateTherapyData, loading: updateTherapyLoading } =
    useApi();
  const { deleteData: deleteTherapyData, loading: deleteTherapyLoading, } = 
    useApi();
  const { postData: addTherapyData, loading: addTherapyLoading } = useApi();
  const [therapies, setTherapies] = useState<Therapy[]>([]);
  const [error, setError] = useState<string | null>();

  const resetError = () => setError(null);

  const onFailure = (errorMessage: string) => {
    setError(errorMessage);
  };

  const getTherapies = async (onSuccess?: (response: Therapy[]) => void) => {
    resetError();
    await getData(
      API_ROUTES.THERAPIES.base,
      (value) => {
        const response = ((value as Therapy[]) ?? [])
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((item, index) => {
            item.index = index + 1;
            return item;
          });
        setTherapies(response);
        onSuccess?.(response);
      },
      onFailure
    );
  };

  const getSessionEnabledTherapies = async (
    onSuccess?: (response: Therapy[]) => void
  ) => {
    resetError();
    await getData(
      API_ROUTES.THERAPIES.sessionEnabled,
      (value) => {
        const response = ((value as Therapy[]) ?? [])
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((item, index) => {
            item.index = index + 1;
            return item;
          });
        onSuccess?.(response);
      },
      onFailure
    );
  };

  const updateTherapy = async (
    therapy: Therapy,
    onSuccess?: (response: Therapy) => void
  ) => {
    resetError();
    await updateTherapyData(
      `${API_ROUTES.THERAPIES.base}/${therapy.id}`,
      therapy,
      onSuccess,
      onFailure
    );
  };

  const deleteTherapy = async (
    therapyId: string,
    onSuccess?: () => void
  ) => {
    setError("");
    await deleteTherapyData(
      `${API_ROUTES.THERAPIES.base}/${therapyId}`,
      {},
      () => {
        onSuccess?.();
        // Refresh the Therapy list
        getTherapies();
      },
      setError
    );
  };

  const addTherapy = async (
    therapy: Therapy,
    onSuccess?: (newTherapy: Therapy) => void
  ) => {
    resetError();
    await addTherapyData(
      API_ROUTES.THERAPIES.base,
      therapy,
      onSuccess,
      onFailure
    );
  };

  const getTherapyAnalyticsDetails = async (
    id: string,
    onSuccess?: (therapyAnalyticsDetails: TherapyAnalyticsDetails) => void
  ) => {
    try {
      await getTherapyAnalyticsDetailsRequest(
        `${API_ROUTES.ANALYTICS.base}${API_ROUTES.ANALYTICS.therapies}/${id}`,
        onSuccess,
        setError
      );
    } catch (error) {
      setError(error.message);
    }
  };

  const getAllTherapyAnalytics = async (
    onSuccess?: (therapyAnalytics: TherapyAnalytics[]) => void
  ) => {
    try {
      await getAllTherapyAnalyticsRequest(
        `${API_ROUTES.ANALYTICS.base}${API_ROUTES.ANALYTICS.therapies}/all`,
        onSuccess,
        setError
      );
    } catch (error) {
      setError(error.message);
    }
  };

  const getPractitionerTherapies = async (
    practitionerId: string,
    onSuccess?: (response: PractitionerTherapy[]) => void
  ) => {
    resetError();

    await getPractitionerTherapiesRequest(
      `${API_ROUTES.THERAPIES.base}?practitionerId=${practitionerId}`,
      onSuccess,
      onFailure
    );
  };

  const getTherapyImage = async (
    therapyId: string,
    onSuccess?: (url: string) => void
  ) => {
    // Query docLink to get the profile pic URL
    const docLinkDetails: Partial<DocLinkDetails> = {
      parentId: therapyId,
      path: `${DMS_PATHS.IMAGES.BASE}${DMS_PATHS.IMAGES.THERAPY}/${therapyId}`,
    };

    getDocLink(docLinkDetails.parentId, docLinkDetails.path, (response) =>
      onSuccess(response?.[0]?.url)
    );
  };

  const setTherapyImage = async (
    therapyId: string,
    therapyImage: File,
    onSuccess?: (url: string) => void
  ) => {
    const docLinkDetails: DocLinkDetails = {
      parentId: therapyId,
      parentType: "therapy",
      path: `${DMS_PATHS.IMAGES.BASE}${DMS_PATHS.IMAGES.THERAPY}/${therapyId}`,
      title: therapyImage.name,
      url: "", // <-- This will be updated after upload
    };

    // Compress file
    const compressedFile = await DMSServices.compressImage(therapyImage);

    // Upload file to DMS
    DMSServices.uploadDocument(
      compressedFile,
      docLinkDetails.parentId,
      docLinkDetails.parentType,
      docLinkDetails.path,
      "",
      (response) => {
        // Create DocLink
        docLinkDetails.url = response.url;
        createDocLink(docLinkDetails, () => onSuccess(docLinkDetails.url));
      }
    );
  };

  return {
    error: error || docLinksError,
    loading:
      getDataLoading ||
      updateTherapyLoading ||
      deleteTherapyLoading ||
      addTherapyLoading ||
      getTherapyAnalyticsDetailsLoading ||
      getAllTherapyAnalyticsRequestLoading ||
      getPractitionerTherapiesLoading ||
      docLinksLoading,
    therapies,
    getTherapies,
    getSessionEnabledTherapies,
    updateTherapy,
    deleteTherapy,
    addTherapy,
    resetError,
    getTherapyAnalyticsDetails,
    getAllTherapyAnalytics,
    getPractitionerTherapies,
    getTherapyImage,
    setTherapyImage,
  };
};
export default useTherapies;
